import * as t from "io-ts";
import { UUID as uuidCodec } from "io-ts-types/UUID";

import { stringEnumCodec } from "../codecs";
import {
  donationChargeResponseCodec,
  nonprofitResponseCodec,
  personalDonationChargeResponseCodec,
  userResponseCodec,
  donationBoostResponseCodec,
  donationResponseCodec,
  tagResponseCodec,
} from "../codecs/entities";
import { nonNegativeIntegerFromStringCodec } from "../codecs/number";
import { usernameCodec } from "../codecs/username";
import { ShareMedium } from "../entity/types";
import { HttpMethod } from "../helpers/http";

import { DonationMatchStatus } from "./donate";

import { makeCachedRouteSpec, makeRouteSpec } from ".";

export const likeDonationRouteSpec = makeRouteSpec({
  path: "/donation/:id/like",
  method: HttpMethod.POST,
  authenticated: true,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: t.type({}),
});

export const unlikeDonationRouteSpec = makeRouteSpec({
  path: "/donation/:id/like",
  method: HttpMethod.DELETE,
  authenticated: true,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: t.type({}),
});

const getDonationResponseBodyCodec = t.intersection([
  t.type({
    donationCharge: t.union([
      personalDonationChargeResponseCodec,
      donationChargeResponseCodec,
    ]),
    nonprofit: nonprofitResponseCodec,
    user: userResponseCodec,
    boost: donationBoostResponseCodec,
    nonprofitSupporterCount: t.number,
  }),
  t.partial({
    nonprofitTags: t.array(tagResponseCodec),
    valueRaised: t.nullType, // todo: remove after deploy
  }),
]);
export const getDonationRouteSpec = makeCachedRouteSpec({
  path: "/donation/:identifier/d",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ identifier: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: getDonationResponseBodyCodec,
});

export const getDonationByShortIdRouteSpec = makeCachedRouteSpec({
  // TODO: should it start with `/donation/...` or with `/users/...` ?
  path: "/donation/:username/:nonprofitSlug/:shortId/d",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({
    username: usernameCodec,
    nonprofitSlug: t.string,
    shortId: nonNegativeIntegerFromStringCodec,
  }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: getDonationResponseBodyCodec,
});

const getDonationBoostsResponseBodyCodec = t.intersection([
  t.type({
    // These numbers may be higher than the number of donations / users returned
    // depending on the booster's privacy settings
    totalBoosts: t.number,
    totalBoostingUsers: t.number,
    donations: t.array(donationResponseCodec),
    users: t.array(userResponseCodec),
  }),
  t.partial({
    valueRaised: t.nullType, // todo: remove after deploy
  }),
]);
export const getDonationBoostsRouteSpec = makeCachedRouteSpec({
  path: "/donation/:id/boosts",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: getDonationBoostsResponseBodyCodec,
});

export const trackShareRouteSpec = makeRouteSpec({
  path: "/donation/:id/trackShare",
  method: HttpMethod.POST,
  authenticated: false,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: t.type({
    shareMedium: stringEnumCodec({
      name: "ShareMedium",
      enumObject: ShareMedium,
    }),
  }),
  responseBodyCodec: t.partial({
    matching: stringEnumCodec({
      name: "DonationMatchStatus",
      enumObject: DonationMatchStatus,
    }),
  }),
});

const chariotSuccessWebhookBodyCodec = t.partial({
  grantId: t.string,
  workflowSessionId: t.string,
  amount: t.number,
  userFriendlyId: t.string,
  trackingId: t.string,
  fundId: t.string,
  externalGrantId: t.string,
  fee: t.number,
  coveredFees: t.boolean,
  status: t.string,
});
export type ChariotSuccessBody = t.TypeOf<
  typeof chariotSuccessWebhookBodyCodec
>;
export const chariotSuccessfulFrontendRouteSpec = makeRouteSpec({
  path: "/donation/:id/chariotSuccess",
  method: HttpMethod.POST,
  authenticated: false,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: t.type({}),
  bodyCodec: chariotSuccessWebhookBodyCodec,
  responseBodyCodec: t.type({}),
});
